import Loader from "components/global/Loader";
import NoNotifications from "./NoNotifications";
import Spacer from "components/global/Spacer";
import styled from "styled-components";
import { useClickOutside } from "webrix/hooks";
import {
  getTimeAgo,
} from "utils/utils";
import { useState } from "react";
import { Tab } from "views/posts/PostStyles";
import ImgWrapper from "components/global/ImgWrapper";
import { Link } from "react-router-dom";
import userIcon from "assets/icons/userIconWhite.svg";
import { socialIcons } from "views/socialMedia/ManageChannels";
import PlatformPhoto from "components/global/PlatformPhoto";

const Wrapper = styled.div`
  width: 360px;
  box-shadow: 0px 25px 35px 0px rgba(0, 0, 0, 0.03),
    0px 0px 5px 0px rgba(0, 0, 0, 0.05);
  background-color: #ffffff;
  position: absolute;
  top: 3.6rem;
  right: 0;
  border-radius: 8px;

  .header {
    height: 6rem;
    padding: 0 12px;
  }

  .content {
    max-height: 46.3rem;
    min-height: 24.3rem;
    overflow: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .tab {
    padding-inline: 12px;

    .item {
      width: 50%;
    }
  }

  @media screen and (max-width: 768px) {
    position: fixed;
    top: 6.4rem;
    right: 2.4rem;
    width: calc(100vw - 4.8rem);
    margin: auto;
  }
`;

const NotificationItem = styled.div`
  padding: 24px 12px;
  border-top: 1px solid #e5e7eb;
  gap: 12px;

  &:nth-child(1) {
    border-top: none;
  }

  .message {
    overflow-wrap: anywhere;
  }
`;

const ResourceLink = styled.div`
  border: 1px solid var(--grey_10);
  padding: 6px;
  border-radius: 8px;
  gap: 6px;

  .media {
    width: 16px;
  }

  .text {
    min-width: calc(100% - 16px - 70px - 20px - 18px);
  }

  .dateTime {
    width: 70px;
  }

  .platformData {
    width: 20px;

    .platformImg {
      height: 10px;
      width: 10px;
      left: 12px;
      bottom: 0;
    }
  }
`;

const notificationTypeMap = {
  general: 1,
  mentions: 2,
}

const NotificationList = ({
  notifications,
  setShowNotifications,
  loading,
  lastListElementRef,
  brands
}) => {
  const [activeTab, setActiveTab] = useState("general");

  const handleOnMouseDownCapture = useClickOutside(() => {
    setShowNotifications(false);
  });

  return (
    <Wrapper onMouseDownCapture={handleOnMouseDownCapture}>
      <div className="header justifySpaceBetween flexRow alignCenter">
        <p className="l2 bold">Notifications</p>
      </div>
      <div className="content">
        <Tab className="flexRow stickyTop tab">
          <button
            className={`flexRow alignCenter justifyCenter item${
              activeTab === "general" ? " active" : ""
            }`}
            onClick={() => setActiveTab("general")}
          >
            <span className="l2">General</span>
          </button>
          <button
            className={`flexRow alignCenter justifyCenter item${
              activeTab === "mentions" ? " active" : ""
            }`}
            onClick={() => setActiveTab("mentions")}
          >
            <span className="l2">Mentions</span>
          </button>
        </Tab>
        {notifications?.filter(item => item.data.type === notificationTypeMap[activeTab])?.map((notification, index) => {
          const {
            data: { message, resource_data, resource_type, meta },
            created_at,
          } = notification;
          const { actor } = meta;
          const {
            id,
            media,
            content,
            updated_at: resource_updated_at,
            created_at: resource_created_at,
            brand,
            status,
            platform,
          } = resource_data;

          const brandDetails = brands?.find(item => item.id === brand?.id);
          const platformData = brandDetails?.socials?.socials?.find(channel => {
            return channel.platform === platform
          })

          return (
            <NotificationItem
              key={index}
              ref={
                notifications.length === index + 1 ? lastListElementRef : null
              }
              className="flexRow"
            >
              <ImgWrapper as="button" bg="var(--primary)" size={3.2}>
                {actor?.avatar_thumb ? (
                  <img
                    src={`${actor.avatar_thumb}`}
                    alt="avatar"
                  />
                ) : (
                  <span className="initials textCenter textUppercase textSmall colorWhite">{`${actor?.first_name?.charAt(
                    0
                  ) ?? ""}${actor?.last_name?.charAt(0) ?? ""}`}</span>
                )}
              </ImgWrapper>
              <div>
                <p
                  className="l3 message"
                  dangerouslySetInnerHTML={{ __html: message }}
                ></p>
                {resource_data && content && (
                  <>
                    <Spacer y={0.6} />
                    <ResourceLink className="fullWidth flexRow alignCenter justifySpaceBetween">
                      {!!media?.length && (
                        <div className="flexRow alignCenter justifyCenter media">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M4.80039 3.60156C4.16387 3.60156 3.55342 3.85442 3.10333 4.30451C2.65325 4.75459 2.40039 5.36504 2.40039 6.00156V18.0016C2.40039 18.6381 2.65325 19.2485 3.10333 19.6986C3.55342 20.1487 4.16387 20.4016 4.80039 20.4016H19.2004C19.8369 20.4016 20.4474 20.1487 20.8974 19.6986C21.3475 19.2485 21.6004 18.6381 21.6004 18.0016V6.00156C21.6004 5.36504 21.3475 4.75459 20.8974 4.30451C20.4474 3.85442 19.8369 3.60156 19.2004 3.60156H4.80039ZM19.2004 18.0016H4.80039L9.60039 8.40156L13.2004 15.6016L15.6004 10.8016L19.2004 18.0016Z"
                              fill="#032250"
                            />
                          </svg>
                        </div>
                      )}
                      <div className="colorBodyText textSmall textLeft text">
                        {content.slice(0, 25)}
                        {content.length > 25 ? "..." : ""}
                      </div>
                      <div className="colorBodyText textSmall dateTime">
                        {new Date(resource_updated_at ?? resource_created_at).toLocaleString("en-US", {
                          month: "short",
                          day: "numeric",
                          // year: "numeric",
                          hour: "numeric",
                          minute: "numeric",
                        })}
                      </div>
                      {platformData && (
                        <div className="platformData">
                          <PlatformPhoto>
                            <ImgWrapper size={1.6} imgHeight={"100%"} className="imgWrapper">
                              <img src={platform === "facebook" ? `https://graph.facebook.com/${platformData?.channel?.page?.id}/picture?return_ssl_resources=true` : (platformData?.channel?.page?.photo || userIcon)} alt="avi" />
                            </ImgWrapper>
                            <img
                              src={socialIcons.circle[platform]}
                              className="platformImg"
                              alt="icon"
                            />
                          </PlatformPhoto>
                        </div>
                      )}
                    </ResourceLink>
                  </>
                )}
                <Spacer y={0.6} />
                {notification.created_at && (
                  <p className="textSmall colorBodyText">
                    {getTimeAgo(created_at)}
                  </p>
                )}
                {(status === "failed" || resource_type === "draft") && (
                  <>
                    <Spacer y={1.6} />
                    <Link to={`/dashboard/${resource_type}s/${id}${status === "failed" ? "?isFailed=true" : ""}`} className="colorSecondary l4" onClick={() => setShowNotifications(false)} >View {resource_type}</Link>
                  </>
                )}
              </div>
            </NotificationItem>
          );
        })}
        {!notifications?.length && !loading && <NoNotifications />}
        {loading && (
          <>
            <Spacer y={2.4} />
            <Loader type="spinner" />
          </>
        )}
      </div>
    </Wrapper>
  );
};

export default NotificationList;
